import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { ScoutPostingStatus, ScoutRunStatus, ScoutTaskStatus } from '@repo/types';

/**
 * Review state of a scouted posting.
 *
 * `promoted` is not a stored status — it is `approved` plus a `promoted_at`
 * stamp — but the distinction is the whole point of this page, so it gets its
 * own visual: an approved posting still needs promoting, a promoted one is done.
 */
const POSTING_STYLES: Record<ScoutPostingStatus | 'promoted', { label: string; className: string }> = {
  new: { label: 'New', className: 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-300' },
  approved: { label: 'Approved', className: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300' },
  rejected: { label: 'Rejected', className: 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-300' },
  promoted: { label: 'Promoted', className: 'border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-900 dark:bg-violet-950 dark:text-violet-300' },
};

export function ScoutPostingStatusBadge({
  status,
  promoted = false,
  className,
}: {
  status: ScoutPostingStatus;
  promoted?: boolean;
  className?: string;
}) {
  const style = POSTING_STYLES[promoted ? 'promoted' : status];
  return (
    <Badge variant="outline" className={cn('font-medium', style.className, className)}>
      {style.label}
    </Badge>
  );
}

const RUN_STYLES: Record<ScoutRunStatus, { label: string; className: string }> = {
  running: { label: 'Running', className: 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-300' },
  finished: { label: 'Finished', className: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300' },
  cancelled: { label: 'Cancelled', className: 'border-muted-foreground/20 bg-muted text-muted-foreground' },
};

export function ScoutRunStatusBadge({
  status,
  className,
}: {
  status: ScoutRunStatus;
  className?: string;
}) {
  const style = RUN_STYLES[status];
  return (
    <Badge variant="outline" className={cn('font-medium', style.className, className)}>
      {style.label}
    </Badge>
  );
}

/**
 * One site's outcome within a run.
 *
 * `cancelled` is muted rather than alarming: it means the operator stopped the
 * run before this site's turn, which is no fault of the site. `failed` is the
 * one an operator is looking for.
 */
const TASK_STYLES: Record<ScoutTaskStatus, { label: string; className: string }> = {
  pending: { label: 'Pending', className: 'border-muted-foreground/20 bg-muted text-muted-foreground' },
  running: { label: 'Running', className: 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-300' },
  done: { label: 'Done', className: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300' },
  failed: { label: 'Failed', className: 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-300' },
  cancelled: { label: 'Cancelled', className: 'border-muted-foreground/20 bg-muted text-muted-foreground' },
};

export function ScoutTaskStatusBadge({
  status,
  className,
}: {
  status: ScoutTaskStatus;
  className?: string;
}) {
  const style = TASK_STYLES[status];
  return (
    <Badge variant="outline" className={cn('font-medium', style.className, className)}>
      {style.label}
    </Badge>
  );
}

/** The ATS hosting a career site — plain, informational, not a status. */
export function AtsBadge({ ats, className }: { ats: string; className?: string }) {
  if (!ats || ats === 'bespoke') {
    return <span className={cn('text-xs text-muted-foreground', className)}>—</span>;
  }
  return (
    <Badge variant="outline" className={cn('font-mono text-[10px] font-medium', className)}>
      {ats.replace(/_/g, ' ')}
    </Badge>
  );
}
